import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Lock, MessageSquare, History, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const STATUS_META = {
  open: { label: 'Open', cls: 'bg-[#FFF0ED] text-[#F97D6A]' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-600' },
  on_hold: { label: 'On Hold', cls: 'bg-amber-50 text-amber-700' },
  resolved: { label: 'Resolved', cls: 'bg-green-50 text-green-700' },
};

export const PRIORITY_META = {
  high: { label: 'High', cls: 'bg-red-50 text-red-600' },
  medium: { label: 'Medium', cls: 'bg-slate-100 text-slate-600' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-500' },
};

export const TYPE_LABEL = { issue: 'Issue', improvement: 'Improvement', question: 'Question' };

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Full ticket view. Read-only for coordinators; Platform Admin gets the controls. */
export default function TicketDialog({ ticketId, admin = false, onClose, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [busy, setBusy] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [note, setNote] = useState('');
  const [noteInternal, setNoteInternal] = useState(false);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open this ticket');
      onCloseRef.current();
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const apply = async (payload, key) => {
    setBusy(key);
    try {
      const res = await api.patch(`/tickets/${ticketId}`, payload);
      setTicket(res.data);
      setPendingStatus('');
      setStatusNote('');
      toast.success('Ticket updated');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update this ticket');
    }
    setBusy('');
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setBusy('note');
    try {
      const res = await api.post(`/tickets/${ticketId}/notes`, { body: note.trim(), internal: noteInternal });
      setTicket(res.data);
      setNote('');
      setNoteInternal(false);
      toast.success('Note saved');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the note');
    }
    setBusy('');
  };

  const needsNote = pendingStatus === 'on_hold' || pendingStatus === 'resolved';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
        {!ticket ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#F97D6A]" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] flex flex-wrap items-center gap-2">
                <span data-testid="ticket-detail-number">{ticket.school_ticket_number || ticket.ticket_number}</span>
                {admin && ticket.school_ticket_number && (
                  <span className="text-xs font-normal text-gray-400" data-testid="ticket-detail-global-number">{ticket.ticket_number}</span>
                )}
                <Badge className={`${STATUS_META[ticket.status].cls} border-0 text-[10px]`} data-testid="ticket-detail-status">{STATUS_META[ticket.status].label}</Badge>
                <Badge className={`${PRIORITY_META[ticket.priority].cls} border-0 text-[10px]`} data-testid="ticket-detail-priority">{PRIORITY_META[ticket.priority].label}</Badge>
              </DialogTitle>
              <DialogDescription className="text-[#1F2937] text-base font-semibold pt-1" data-testid="ticket-detail-subject">
                {ticket.subject}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 text-sm">
              <p className="whitespace-pre-wrap text-[#4B5563]" data-testid="ticket-detail-description">{ticket.description}</p>

              {ticket.screenshot_url && (
                <a href={ticket.screenshot_url} target="_blank" rel="noreferrer" data-testid="ticket-detail-screenshot-link">
                  <img src={ticket.screenshot_url} alt="Screenshot" className="rounded-xl border border-gray-200 max-h-64 object-contain" data-testid="ticket-detail-screenshot" />
                </a>
              )}

              <div className="grid grid-cols-2 gap-3 text-xs bg-[#F8F9FA] rounded-xl p-3" data-testid="ticket-detail-meta">
                <div><span className="text-gray-400">Raised by</span><p className="text-[#1F2937] font-medium">{ticket.created_by_name} · {ticket.created_by_role}</p></div>
                <div><span className="text-gray-400">School</span><p className="text-[#1F2937] font-medium">{ticket.school_name || 'Platform wide'} {ticket.school_id ? `(${ticket.school_id})` : ''}</p></div>
                <div><span className="text-gray-400">Type</span><p className="text-[#1F2937] font-medium">{TYPE_LABEL[ticket.type]}</p></div>
                <div><span className="text-gray-400">Environment</span><p className="text-[#1F2937] font-medium" data-testid="ticket-detail-environment">{ticket.environment}</p></div>
                <div><span className="text-gray-400">Raised on</span><p className="text-[#1F2937] font-medium">{formatDate(ticket.created_at)}</p></div>
                <div className="min-w-0">
                  <span className="text-gray-400">Page</span>
                  <p className="truncate"><a href={ticket.page_url} className="text-[#F97D6A] hover:underline inline-flex items-center gap-1" target="_blank" rel="noreferrer" data-testid="ticket-detail-page-url">{ticket.page_url || '—'}<ExternalLink className="h-3 w-3" /></a></p>
                </div>
              </div>

              {admin && (
                <div className="border border-gray-200 rounded-xl p-4 space-y-4" data-testid="ticket-admin-controls">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Priority</Label>
                      <Select value={ticket.priority} onValueChange={(v) => apply({ priority: v }, 'priority')}>
                        <SelectTrigger className="mt-1 rounded-xl" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_META).map(([v, m]) => (
                            <SelectItem key={v} value={v} data-testid={`ticket-priority-${v}`}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <Select value={pendingStatus || ticket.status} onValueChange={(v) => {
                        if (v === 'on_hold' || v === 'resolved') { setPendingStatus(v); setStatusNote(''); }
                        else { setPendingStatus(''); apply({ status: v }, 'status'); }
                      }}>
                        <SelectTrigger className="mt-1 rounded-xl" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_META).map(([v, m]) => (
                            <SelectItem key={v} value={v} data-testid={`ticket-status-${v}`}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {needsNote && (
                    <div data-testid="ticket-status-note-block">
                      <Label className="text-xs text-gray-500">
                        {pendingStatus === 'on_hold' ? 'Why is this on hold?' : 'How was this resolved?'}
                      </Label>
                      <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={3}
                                className="mt-1 rounded-xl" data-testid="ticket-status-note-input" />
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" disabled={!statusNote.trim() || busy === 'status'}
                                onClick={() => apply({ status: pendingStatus, note: statusNote.trim() }, 'status')}
                                className="rounded-xl bg-[#F97D6A] hover:bg-[#e86a57]" data-testid="ticket-status-confirm-btn">
                          {busy === 'status' ? <Loader2 className="h-4 w-4 animate-spin" /> : `Move to ${STATUS_META[pendingStatus].label}`}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPendingStatus('')} className="rounded-xl" data-testid="ticket-status-cancel-btn">Cancel</Button>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs text-gray-500">Add a note</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                              placeholder="What you want to tell the coordinator, or keep internal"
                              className="mt-1 rounded-xl" data-testid="ticket-note-input" />
                    <div className="flex items-center justify-between mt-2 gap-3">
                      <Select value={noteInternal ? 'internal' : 'visible'} onValueChange={(v) => setNoteInternal(v === 'internal')}>
                        <SelectTrigger className="rounded-xl h-9 text-xs w-56" data-testid="ticket-note-visibility-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="visible" data-testid="ticket-note-visible">Visible to the coordinator</SelectItem>
                          <SelectItem value="internal" data-testid="ticket-note-internal">Internal — admin only</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" disabled={!note.trim() || busy === 'note'} onClick={saveNote}
                              className="rounded-xl bg-[#1F2937] hover:bg-[#374151]" data-testid="ticket-note-save-btn">
                        {busy === 'note' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save note'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div data-testid="ticket-notes-list">
                <p className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                  <MessageSquare className="h-3.5 w-3.5" /> Notes
                </p>
                {(ticket.notes || []).length === 0 ? (
                  <p className="text-xs text-gray-400" data-testid="ticket-notes-empty">No notes yet.</p>
                ) : (
                  <div className="space-y-2">
                    {ticket.notes.map((n, i) => (
                      <div key={i} className={`rounded-xl px-3 py-2 ${n.internal ? 'bg-amber-50 border border-amber-100' : 'bg-[#F8F9FA]'}`} data-testid={`ticket-note-${i}`}>
                        <p className="text-[#4B5563] whitespace-pre-wrap">{n.body}</p>
                        <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                          {n.by} · {formatDate(n.at)}
                          {n.internal && <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold"><Lock className="h-3 w-3" />Internal</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div data-testid="ticket-history-list">
                <p className="flex items-center gap-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                  <History className="h-3.5 w-3.5" /> Activity
                </p>
                <ul className="space-y-1.5">
                  {(ticket.history || []).map((h, i) => (
                    <li key={i} className="text-xs text-[#6B7280]" data-testid={`ticket-history-${i}`}>
                      <span className="text-[#1F2937] font-medium">{h.action}</span>
                      {h.detail ? ` — ${h.detail}` : ''}
                      {h.note ? ` (${h.note})` : ''}
                      <span className="text-gray-400"> · {h.by} · {formatDate(h.at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
